/**
 * The live score of a fill in progress: what the filler has earned so far
 * against what the questions they can see are worth, plus the flag count.
 *
 * Walks the answered content through forEachVisibleLeaf, so a question the
 * filler cannot see counts for nothing, exactly as the server scores the
 * submission. Choice answers carry their snapshotted pick scores in the
 * value; the element's maximum comes from its response set (local sets
 * from the content, global ones through the host's resolver). Scorable
 * ratings and sliders score by their number.
 *
 * Used by the designer preview, the web and mobile fill surfaces and the
 * public form when a form shows the filler their score.
 */

import type { LocalDocumentResponseSet, LogicRule } from "./types";
import { forEachVisibleLeaf } from "./logic/visibility";
import { scalarScoringConfig, scoreScalarAnswer } from "./scalarScoring";
import { unwrapEnvelope } from "./valueEnvelope";

export interface LiveScoreSet {
  type: "MULTI_CHOICE" | "TABLE";
  isScoring: boolean;
  maxScoreOverride: number | null;
  responses: Array<{ score?: number | null }>;
  tableRows: Array<{ values: Record<string, unknown> }>;
  scoreColumnKey: string | null;
}

/** The shape a global response set arrives in from the API. */
export interface LiveScoreGlobalSet {
  type?: string | null;
  isScoring?: boolean | null;
  maxScoreOverride?: number | null;
  responses?: Array<{ score?: number | null }> | null;
  tableRows?: Array<{ values: Record<string, unknown> }> | null;
  scoreColumnKey?: string | null;
}

export interface LiveScore {
  currentScore: number;
  maxScore: number;
  /** Rounded to one decimal; null until a scoring question is on the form. */
  scorePercent: number | null;
  flagCount: number;
  sawScoring: boolean;
  /**
   * True when at least one element references a scoring response set the
   * host could not resolve. The max and percent are then a lower bound;
   * show a `~` so the filler knows.
   */
  approximate: boolean;
}

/**
 * Highest single positive score in the response set: the per-pick
 * ceiling. `maxScoreOverride` wins when set.
 */
export function maxSingleScoreForSet(set: LiveScoreSet): number {
  if (!set.isScoring) return 0;
  if (typeof set.maxScoreOverride === "number" && Number.isFinite(set.maxScoreOverride)) {
    return set.maxScoreOverride;
  }
  let best = 0;
  if (set.type === "TABLE" && set.scoreColumnKey) {
    for (const row of set.tableRows) {
      const raw = row.values?.[set.scoreColumnKey];
      const n = typeof raw === "number" ? raw : Number(raw);
      if (Number.isFinite(n) && n > best) best = n;
    }
    return best;
  }
  for (const r of set.responses) {
    const n = typeof r.score === "number" ? r.score : Number(r.score as unknown);
    if (Number.isFinite(n) && n > best) best = n;
  }
  return best;
}

export function localSetForLiveScore(set: LocalDocumentResponseSet): LiveScoreSet {
  return {
    type: (set.type || "MULTI_CHOICE") as "MULTI_CHOICE" | "TABLE",
    isScoring: !!set.isScoring,
    maxScoreOverride: set.maxScoreOverride ?? null,
    responses: (set.responses || []) as Array<{ score?: number | null }>,
    tableRows: (set.tableRows || []) as Array<{ values: Record<string, unknown> }>,
    scoreColumnKey: set.scoreColumnKey ?? null,
  };
}

export function globalSetForLiveScore(set: LiveScoreGlobalSet): LiveScoreSet {
  return {
    type: set.type === "TABLE" ? "TABLE" : "MULTI_CHOICE",
    isScoring: !!set.isScoring,
    maxScoreOverride: set.maxScoreOverride ?? null,
    responses: set.responses || [],
    tableRows: set.tableRows || [],
    scoreColumnKey: set.scoreColumnKey ?? null,
  };
}

type RichEntry = { score?: number | null; flagged?: boolean | null; other?: boolean; none?: boolean };

const isRichEntryArray = (v: unknown): v is RichEntry[] =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every(
    (e) =>
      e !== null &&
      typeof e === "object" &&
      !Array.isArray(e) &&
      ("score" in (e as object) || "flagged" in (e as object)),
  );

export interface LiveScoreContent {
  elements?: unknown[] | undefined;
  logic?: LogicRule[] | undefined;
  responseSets?: LocalDocumentResponseSet[] | undefined;
}

/**
 * @param resolveGlobalSet  The host's lookup for a response set id that is
 *   not local to the content; return null when it is not known yet.
 */
export function computeLiveScore(
  content: LiveScoreContent,
  values: Record<string, unknown>,
  resolveGlobalSet: (id: string) => LiveScoreGlobalSet | null | undefined = () => null,
): LiveScore {
  let currentScore = 0;
  let maxScore = 0;
  let sawScoring = false;
  let flagCount = 0;
  let approximate = false;

  const localById = new Map<string, LocalDocumentResponseSet>();
  for (const s of content.responseSets || []) localById.set(s.id, s);
  const globalCache = new Map<string, LiveScoreSet | null>();
  const resolveSet = (id: string): LiveScoreSet | null => {
    const local = localById.get(id);
    if (local) return localSetForLiveScore(local);
    if (globalCache.has(id)) return globalCache.get(id) ?? null;
    const found = resolveGlobalSet(id);
    const resolved = found ? globalSetForLiveScore(found) : null;
    globalCache.set(id, resolved);
    return resolved;
  };

  forEachVisibleLeaf(
    { elements: content.elements as never, logic: content.logic },
    values,
    ({ element: el, values: scopeValues }) => {
      const data = (el.data as Record<string, unknown> | undefined) || {};

      // A scorable rating or slider: its number is the score.
      const scalar = scalarScoringConfig(el.typeId, data);
      if (scalar) {
        sawScoring = true;
        const scored = scoreScalarAnswer(scalar, unwrapEnvelope(scopeValues[el.id]));
        maxScore += scored.max;
        if (scored.score !== null) currentScore += scored.score;
        if (scored.flagged) flagCount++;
      }

      const responseSetId = data.responseSetId as string | undefined;
      if (!responseSetId) return;

      // Sum vs best is a per-element choice on multi-select dropdowns
      // (the `scoringMode` radio-cards field, a rich-entry array). A
      // single-pick element can't accumulate. Defaults to 'best'.
      const allowMultiple = !!data.allowMultiple;
      const modeEntries = Array.isArray(data.scoringMode) ? (data.scoringMode as Array<{ value?: unknown }>) : [];
      const modeValue =
        modeEntries[0] && typeof modeEntries[0].value === "string" ? (modeEntries[0].value as string) : "best";
      const mode: "best" | "sum" = allowMultiple && modeValue === "sum" ? "sum" : "best";

      const v = unwrapEnvelope(scopeValues[el.id]);
      let scoredPickCount = 0;
      // Picks carrying a finite score. A null score is a non-scoring
      // response (an "N/A" option is the common case): it drops out of
      // both numerator and denominator.
      let finiteScorePickCount = 0;
      if (isRichEntryArray(v)) {
        let elementScore = 0;
        for (const e of v) {
          const isSentinel = e.other === true || e.none === true;
          if (!isSentinel) scoredPickCount++;
          const hasScore = e.score !== null && e.score !== undefined;
          const n = hasScore ? (typeof e.score === "number" ? e.score : Number(e.score as unknown)) : NaN;
          if (hasScore && Number.isFinite(n)) {
            finiteScorePickCount++;
            elementScore = mode === "sum" ? elementScore + n : Math.max(elementScore, n);
          }
          if (e.flagged === true) flagCount++;
        }
        currentScore += elementScore;
      }

      const set = resolveSet(responseSetId);
      if (!set) {
        if (!responseSetId.startsWith("local_")) approximate = true;
        return;
      }
      if (!set.isScoring) return;
      sawScoring = true;
      const maxSingle = maxSingleScoreForSet(set);
      if (mode === "sum") {
        // The denominator scales with how many applicable options were
        // picked, so the percent stays bounded as picks accumulate.
        maxScore += finiteScorePickCount * maxSingle;
      } else {
        // Best mode takes the per-pick ceiling. Answered only with
        // non-scoring responses, the question does not apply (0/0, not
        // 0/1). Unanswered, it still contributes its max.
        const answeredNonScoringOnly = scoredPickCount > 0 && finiteScorePickCount === 0;
        if (!answeredNonScoringOnly) maxScore += maxSingle;
      }
    },
  );

  const scorePercent = sawScoring && maxScore > 0 ? Math.round((currentScore / maxScore) * 1000) / 10 : null;
  return { currentScore, maxScore, scorePercent, flagCount, sawScoring, approximate };
}

/** "12/20 · 60%" style text for a score chip; `~` when approximate. */
export function formatLiveScore(score: LiveScore): string {
  const tilde = score.approximate ? "~" : "";
  const pct = score.scorePercent !== null ? ` · ${tilde}${score.scorePercent}%` : "";
  return `${tilde}${score.currentScore}/${score.maxScore}${pct}`;
}
